import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../api/client'
import { useForbiddenGuard, useWriteForbiddenGuard } from '../api/guard'
import { titledErrorMessage } from '../api/problems'
import { fmtTime } from '../api/mcpUpstreams'
import {
  DIRECTORY_CATEGORY_RE,
  DIRECTORY_QUERY_KEY as QUERY_KEY,
  DIRECTORY_SLUG_RE as SLUG_RE,
  type AuthKind,
  type DirectoryEntry,
  type DirectoryEntryBody,
  type DirectoryEntryWrite,
  type DirectoryListing,
  type DirectoryStatus,
} from '../api/mcpDirectory'
import { ConfirmDialog } from '../components/ConfirmDialog'
import './screens.css'

interface EntryForm {
  slug: string
  name: string
  mcp_url: string
  auth_kind: AuthKind
  category: string
  official: boolean
  setup_steps_md: string
  vendor_url: string
  status: DirectoryStatus
  connector_name: string
}

const EMPTY: EntryForm = {
  slug: '',
  name: '',
  mcp_url: '',
  auth_kind: 'none',
  category: '',
  official: false,
  setup_steps_md: '',
  vendor_url: '',
  status: 'draft',
  connector_name: '',
}

function toForm(e: DirectoryEntry): EntryForm {
  return {
    slug: e.slug,
    name: e.name,
    mcp_url: e.mcp_url,
    auth_kind: e.auth_kind,
    category: e.category ?? '',
    official: e.official ?? false,
    setup_steps_md: e.setup_steps_md ?? '',
    vendor_url: e.vendor_url ?? '',
    status: e.status,
    connector_name: e.connector_name ?? '',
  }
}

function toBody(f: EntryForm): DirectoryEntryBody {
  return {
    name: f.name.trim(),
    mcp_url: f.mcp_url.trim(),
    auth_kind: f.auth_kind,
    category: f.category.trim() || null,
    official: f.official,
    setup_steps_md: f.setup_steps_md,
    vendor_url: f.vendor_url.trim() || null,
    status: f.status,
    connector_name: f.connector_name.trim() || null,
  }
}

export function McpDirectoryView() {
  const qc = useQueryClient()
  const [form, setForm] = useState<EntryForm>(EMPTY)
  const [editing, setEditing] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<DirectoryEntry | null>(null)
  const [okMsg, setOkMsg] = useState<string | null>(null)
  const set = <K extends keyof EntryForm>(k: K, v: EntryForm[K]) =>
    setForm((f) => ({ ...f, [k]: v }))

  const query = useQuery<DirectoryListing>({
    queryKey: QUERY_KEY,
    queryFn: () => apiFetch('/admin/directory/mcp'),
  })
  useForbiddenGuard(query.error)

  const save = useMutation({
    mutationFn: (f: EntryForm) =>
      apiFetch<DirectoryEntryWrite>(
        `/admin/directory/mcp/${encodeURIComponent(f.slug.trim())}`,
        { method: 'PUT', body: JSON.stringify(toBody(f)) },
      ),
    onSuccess: (data) => {
      setOkMsg(`${data.slug} ${data.created ? 'created' : 'updated'}.`)
      setForm(EMPTY)
      setEditing(null)
      qc.invalidateQueries({ queryKey: QUERY_KEY })
    },
    onError: () => setOkMsg(null),
  })
  useWriteForbiddenGuard(save.error)

  const remove = useMutation({
    mutationFn: (slug: string) =>
      apiFetch(`/admin/directory/mcp/${encodeURIComponent(slug)}`, { method: 'DELETE' }),
    onSuccess: (_d, slug) => {
      setOkMsg(`${slug} deleted.`)
      if (editing === slug) {
        setEditing(null)
        setForm(EMPTY)
      }
      qc.invalidateQueries({ queryKey: QUERY_KEY })
    },
    onError: () => setOkMsg(null),
  })
  useWriteForbiddenGuard(remove.error)

  const entries = query.data?.entries ?? []
  const categoryOk = form.category.trim() === '' || DIRECTORY_CATEGORY_RE.test(form.category.trim())
  const valid =
    SLUG_RE.test(form.slug.trim()) &&
    form.name.trim() !== '' &&
    form.mcp_url.trim().startsWith('https://') &&
    categoryOk &&
    (form.vendor_url.trim() === '' || form.vendor_url.trim().startsWith('https://'))

  return (
    <section className="scr" aria-labelledby="mcpdir-heading">
      <div className="screen-head">
        <h2 id="mcpdir-heading">MCP Directory</h2>
      </div>
      <p className="scr-note">
        Display-only list of vendor MCP servers. URLs are stored and shown, never fetched by
        the gateway; setup steps are rendered as plain text. Only <strong>active</strong>{' '}
        entries are public.
      </p>

      {query.isError && (query.error as { status?: number }).status !== 403 && (
        <p className="screen-error" role="alert">
          Failed to load the MCP directory.
        </p>
      )}
      {remove.isError && (
        <p className="screen-error" role="alert">
          {titledErrorMessage(remove.error)}
        </p>
      )}
      {okMsg && <p className="scr-ok">{okMsg}</p>}

      <table className="scr-table" aria-label="MCP directory entries">
        <thead>
          <tr>
            <th>Entry</th>
            <th>Category</th>
            <th>MCP URL</th>
            <th>Auth</th>
            <th>Status</th>
            <th>Linked connector</th>
            <th>Updated</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => (
            <tr key={e.slug} className={e.status === 'retired' ? 'scr-row--muted' : undefined}>
              <td>
                {e.name}
                {e.official && (
                  <>
                    {' '}
                    <span className="scr-badge scr-badge--active">official</span>
                  </>
                )}
                <div className="scr-muted scr-mono">{e.slug}</div>
              </td>
              <td className="scr-mono">{e.category ?? '—'}</td>
              {/* Shown as text, not a link: the directory is display-only. */}
              <td className="scr-mono">{e.mcp_url}</td>
              <td>{e.auth_kind}</td>
              <td>
                <span
                  className={`scr-badge scr-badge--${e.status === 'active' ? 'active' : e.status === 'draft' ? 'pending' : 'deprecated'}`}
                >
                  {e.status}
                </span>
              </td>
              {/* Read-only: derived server-side from the linked connector. */}
              <td>
                {e.connector_name ? (
                  <>
                    <span className="scr-mono">{e.connector_name}</span>
                    <div className="scr-muted">
                      {e.connector_source ?? '—'} · {e.connector_auth_kind ?? '—'}
                    </div>
                  </>
                ) : (
                  '—'
                )}
              </td>
              <td>{fmtTime(e.updated_at)}</td>
              <td>
                <span className="scr-actions">
                  <button
                    type="button"
                    className="screen-btn"
                    aria-label={`Edit ${e.slug}`}
                    onClick={() => {
                      setEditing(e.slug)
                      setForm(toForm(e))
                      setOkMsg(null)
                    }}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="screen-btn"
                    aria-label={`Delete ${e.slug}`}
                    disabled={remove.isPending}
                    onClick={() => setDeleting(e)}
                  >
                    Delete
                  </button>
                </span>
              </td>
            </tr>
          ))}
          {!query.isLoading && entries.length === 0 && (
            <tr>
              <td colSpan={8} className="ws-empty">
                No directory entries.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="scr-panel">
        <h3>{editing ? `Edit ${editing}` : 'New directory entry'}</h3>
        <form
          className="scr-form"
          aria-label="Directory entry"
          onSubmit={(ev) => {
            ev.preventDefault()
            if (valid) save.mutate(form)
          }}
        >
          <label>
            Slug
            <input
              className="scr-input"
              value={form.slug}
              disabled={editing !== null}
              placeholder="vendor-name"
              onChange={(e) => set('slug', e.target.value)}
            />
          </label>
          <label>
            Name
            <input
              className="scr-input"
              maxLength={120}
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
            />
          </label>
          <label>
            MCP URL (https)
            <input
              className="scr-input"
              value={form.mcp_url}
              onChange={(e) => set('mcp_url', e.target.value)}
            />
          </label>
          <label>
            Auth kind
            <select value={form.auth_kind} onChange={(e) => set('auth_kind', e.target.value as AuthKind)}>
              <option value="none">none</option>
              <option value="api_key">api_key</option>
              <option value="oauth">oauth</option>
            </select>
          </label>
          <label>
            Category (slug, optional)
            <input
              className="scr-input"
              value={form.category}
              placeholder="e.g. seo, crm, automation"
              maxLength={32}
              aria-invalid={!categoryOk}
              onChange={(e) => set('category', e.target.value)}
            />
            {!categoryOk && (
              <span className="scr-err">Lowercase slug: a letter, then 1–31 of a-z, 0-9, _.</span>
            )}
          </label>
          <label className="scr-check">
            <input
              type="checkbox"
              checked={form.official}
              onChange={(e) => set('official', e.target.checked)}
            />
            Official (the vendor runs this MCP server)
          </label>
          <label>
            Vendor URL (https, optional)
            <input
              className="scr-input"
              value={form.vendor_url}
              onChange={(e) => set('vendor_url', e.target.value)}
            />
          </label>
          <label>
            Status
            <select value={form.status} onChange={(e) => set('status', e.target.value as DirectoryStatus)}>
              <option value="draft">draft</option>
              <option value="active">active</option>
              <option value="retired">retired</option>
            </select>
          </label>
          <label>
            Proxying connector (optional)
            <input
              className="scr-input"
              value={form.connector_name}
              placeholder="connector name"
              onChange={(e) => set('connector_name', e.target.value)}
            />
          </label>
          <label style={{ gridColumn: '1 / -1' }}>
            Setup steps (markdown, shown as text)
            <textarea
              className="scr-textarea"
              rows={5}
              maxLength={20000}
              value={form.setup_steps_md}
              onChange={(e) => set('setup_steps_md', e.target.value)}
            />
          </label>
          <div className="scr-panel-actions">
            <button type="submit" className="screen-btn screen-btn-primary" disabled={!valid || save.isPending}>
              {editing ? 'Save entry' : 'Create entry'}
            </button>
            {editing && (
              <button
                type="button"
                className="screen-btn"
                onClick={() => {
                  setEditing(null)
                  setForm(EMPTY)
                }}
              >
                Cancel edit
              </button>
            )}
          </div>
        </form>
        {save.isError && (
          <p className="scr-err" role="alert">
            {titledErrorMessage(save.error)}
          </p>
        )}
      </div>

      {deleting && (
        <ConfirmDialog
          label="Confirm directory delete"
          confirmText="Delete"
          onCancel={() => setDeleting(null)}
          onConfirm={() => {
            setOkMsg(null)
            remove.mutate(deleting.slug)
            setDeleting(null)
          }}
        >
          <p>
            Permanently delete the directory entry <strong>{deleting.name}</strong> (
            <span className="scr-mono">{deleting.slug}</span>)? Set it to retired instead to keep
            it.
          </p>
        </ConfirmDialog>
      )}
    </section>
  )
}
